import { create, all } from 'mathjs';

// initialize mathjs and base unit
const math = create(all);
math.createUnit('u');

// Normalize unicode primes and superscripts
export const normalizeSymbols = (str) => {
  if (typeof str !== 'string') return str;
  return str
    .replace(/\u2032/g, "'") // ′
    .replace(/\u2033/g, '"') // ″
    .replace(/²/g, '^2')
    .replace(/³/g, '^3');
};

// Convert imperial notations to mathjs units inside an arbitrary string
export const toMathjsImperial = (input) => {
  if (input == null) return input;
  let str = String(input).trim();
  if (!str) return str;

  str = normalizeSymbols(str);

  // feet + inches, allow space or hyphen separator: 16' 4" | 16'-4" | -16' 4.5"
  const feetInches = /([+-]?\d+(?:\.\d+)?)\s*'\s*[-\s]*([+-]?\d+(?:\.\d+)?)\s*"?/g;
  str = str.replace(feetInches, (_, f, i) => {
    const fNum = parseFloat(f);
    const iNum = parseFloat(i);
    const op = (String(i).trim().startsWith('-') || fNum < 0) ? '-' : '+';
    const iAbs = Math.abs(iNum);
    // wrap in parentheses to preserve precedence in larger expressions
    return `(${f} ft ${op} ${iAbs} in)`;
  });

  // standalone feet: 10'
  const feetOnly = /([+-]?\d+(?:\.\d+)?)\s*'/g;
  str = str.replace(feetOnly, (_, f) => `${f} ft`);

  // standalone inches: 8"
  const inchesOnly = /([+-]?\d+(?:\.\d+)?)\s*"/g;
  str = str.replace(inchesOnly, (_, i) => `${i} in`);

  // common area/unit variants
  str = str.replace(/\bft²\b/g, 'ft^2').replace(/\bin²\b/g, 'in^2');

  return str;
};

export const evaluateFormula = (formula, context = {}) => {
  const scope = {};
  for (const key in context) {
    const value = context[key];
    const sanitizedKey = key.replace(/[^A-Z0-9]/gi, '');
    if (!sanitizedKey) continue;

    try {
      if (value && value.isUnit) {
        scope[sanitizedKey] = value;
      } else if (typeof value === 'number') {
        scope[sanitizedKey] = value;
      } else if (typeof value === 'string') {
        const normalized = toMathjsImperial(value);
        try {
          scope[sanitizedKey] = math.evaluate(normalized);
        } catch {
          try {
            scope[sanitizedKey] = math.unit(normalized);
          } catch {
            scope[sanitizedKey] = normalized;
          }
        }
      } else {
        scope[sanitizedKey] = value;
      }
    } catch (e) {
      scope[sanitizedKey] = value;
    }
  }

  const expr = toMathjsImperial(formula.startsWith('=') ? formula.slice(1) : formula);
  let result = math.evaluate(expr, scope);
  

  
  // Format numbers to 2 decimal places
  if (typeof result === 'number') {
    result = Math.round(result * 100) / 100;
  } else if (result && result.isUnit) {
    // Round unit values while preserving unit
    const roundedValue = Math.round(result.toNumber() * 100) / 100;
    const unitStr = result.toString().split(' ').slice(1).join(' ');
    result = math.unit(roundedValue, unitStr);
  }
  
  return result;
};

export const resolveAllParams = (shapeParams = {}, calculatedParams = {}) => {
  let resolved = { ...calculatedParams };
  let formulasToSolve = {};

  // Separate static values from formulas
  for (const key in shapeParams) {
    if (key === 'order' || typeof shapeParams[key] === 'object') continue;
    const value = shapeParams[key];
    if (typeof value === 'string' && value.startsWith('=')) {
      formulasToSolve[key] = value;
    } else {
      resolved[key] = value;
    }
  }

  const maxIterations = 10;
  let i = 0;
  let lastUnresolved = -1;

  while (Object.keys(formulasToSolve).length > 0 && i < maxIterations) {
    const currentUnresolved = Object.keys(formulasToSolve).length;
    if (lastUnresolved === currentUnresolved) break; // no progress
    lastUnresolved = currentUnresolved;

    const stillUnresolved = {};

    for (const key in formulasToSolve) {
      const formula = formulasToSolve[key];
      try {
        let result = evaluateFormula(formula, resolved);
        // Round numeric results and units to 2 decimal places
        if (typeof result === 'number') {
          result = Math.round(result * 100) / 100;
        } else if (result && result.isUnit) {
          // Round unit values while preserving unit
          const roundedValue = Math.round(result.toNumber() * 100) / 100;
          const unitStr = result.toString().split(' ').slice(1).join(' ');
          result = math.unit(roundedValue, unitStr);
        }
        resolved[key] = result;
      } catch (e) {
        stillUnresolved[key] = formula;
      }
    }
    formulasToSolve = stillUnresolved;
    i++;
  }

  for (const key in formulasToSolve) {
    resolved[key] = { error: '#REF!' };
  }

  return resolved;
};

export { math };

export default {
  normalizeSymbols,
  toMathjsImperial,
  evaluateFormula,
  resolveAllParams,
  math,
};